import { BlogNewsListCardData } from '../blog-news/blog-news';
import { EventListCardData } from '../event/event-types';
import { isOnDemandEvent } from '../events/helpers/is-on-demand-event';
import { ContentHubProps } from './content-hub-types';
import type { PostType } from './query';

export type ListCardData = BlogNewsListCardData | EventListCardData;

type SluggedItem = { slug?: string; permalink?: string; key?: string };

export const getSlug = (item: SluggedItem) => item?.slug || item?.permalink || item?.key;

export const isActiveEvent = (event: EventListCardData) => {
    const date = event?.eventExpireDate || event?.eventDate;
    if (!date) {
        return true;
    }
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const eventDate = new Date(date);
    eventDate.setHours(0, 0, 0, 0);
    return eventDate >= today;
};

export const sortOnDemandEventsFirst = (data: ListCardData[]) => {
    const onDemand: EventListCardData[] = [];
    const rest: ListCardData[] = [];
    data.forEach((item) => {
        if (isOnDemandEvent(item as EventListCardData)) {
            onDemand.push(item as EventListCardData);
        } else {
            rest.push(item);
        }
    });
    onDemand.sort((a, b) => (b?.date ? +new Date(b.date) : 0) - (a?.date ? +new Date(a.date) : 0));
    return [...onDemand, ...rest];
};

/** Walks `data` in order and returns the first item that:
 *  - has the requested `tag` (or any tag, if `tag === 'NO_TAG'`)
 *  - has a slug not already present in `rendered`
 *  Returns null when nothing qualifies. */
export const findNextUniquePost = (
    tag: string,
    data: ListCardData[],
    rendered: string[],
) => {
    const filtered = tag === 'NO_TAG'
        ? data
        : data.filter((c) => ((c as { tags?: string[] })?.tags ?? []).includes(tag));

    for (const candidate of filtered) {
        if (!candidate) {
            break;
        }
        const slug = getSlug(candidate);
        if (slug && !rendered.includes(slug)) {
            return candidate;
        }
    }
    return null;
};

/** Bind audience + events filters to a specific `ContentHubProps` instance. The
 *  filters close over `props`, so callers don't have to thread audience /
 *  on-demand settings through every call site. */
export const createFilters = (props: ContentHubProps) => {
    const audienceFilter = (post: { isHealthcareProfessional?: boolean }) => {
        if (!props.audience || props.audience === 'all') {
            return true;
        }
        if (props.audience === 'hcp') {
            return Boolean(post?.isHealthcareProfessional);
        }
        return !post?.isHealthcareProfessional;
    };

    const eventsFilter = (event: EventListCardData, cardIndex: number, type: PostType) => {
        if (type === 'blog-and-news') {
            return true;
        }
        // "Unlisted" events stay out of every hub feed (reachable by direct link only).
        if (event?.hideFromEventsListing) {
            return false;
        }
        if (event?.isFullyBooked) {
            return false;
        }
        if (!isActiveEvent(event)) {
            return false;
        }
        if (isOnDemandEvent(event)) {
            if (props.contentType?.posttype?.value === 'event' && !props.globalIncludeAndPreferOnDemandEvents) {
                return false;
            }
            if (props.mixedContent && !props.cards[cardIndex]?.includeAndPreferOnDemandEvents) {
                return false;
            }
        }
        return true;
    };

    return { audienceFilter, eventsFilter };
};

export type Filters = ReturnType<typeof createFilters>;
